import { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"

import { useLazyCreateContractorQuery } from "store/api/contractorsApi"
import { useAppDispatch } from "store/hooks"
import { setCurrentZip } from "store/tempDataSlise"
import { UITypes } from "types"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import { ReactComponent as IconContractor } from "assets/svg/modal-icon-contractor.svg"

import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import type { ContractorPartnersFormHandle } from "components/ContractorPartnersForm"

import { ContractorModalContent } from "../components/contractorModalContent"
import styles from "../EditContractorModal/index.module.scss"

const inputValuesInitialState: UITypes.Contractor = {
  name: "",
  brand: "",
  parentCompany: [],
  address1: "",
  address2: "",
  zip: [],
  state: [],
  city: [],
  suburb: [],
  url: "",
  schedule: "",
  stScheduleFlag: false,
  fullName: "",
  phone: "",
  email: "",
  alertEmail: "",
  emailAlerts: true,
  isDisable: false,
  appStoreLink: "",
  googlePlayLink: "",
  phoneForSupport: true,
  phoneForSupportCall: "",
  phoneForSupportText: "",
  stClientID: "",
  stClientSecret: "",
  stAppKey: "",
  stTenant: "",
  subscriptionPlan: SubscriptionPlan.START,
  stripeCustomerId: "",
  hubspotDealId: "",
  accountStatus: "1",
}

interface Props {
  onCancel: () => void
  onSuccess: () => void
}

export const AddContractorModal = ({ onCancel, onSuccess }: Props) => {
  const dispatch = useAppDispatch()
  const [triggerCreateContractor, { isFetching }] = useLazyCreateContractorQuery()
  const partnersFormRef = useRef<ContractorPartnersFormHandle>(null)
  const [isSavingPartners, setIsSavingPartners] = useState(false)

  const onSubmit = async (data: FormData, _input: UITypes.Contractor) => {
    try {
      const res = await triggerCreateContractor(data)

      if (res.error) {
        const errData = res.error as { data?: { errors?: Record<string, string[]>; message?: string } }
        const errors = Object.keys(errData?.data?.errors || {})
          .map(key => errData?.data?.errors?.[key]?.[0])
          .filter(Boolean)
          .join(", ")

        toast.error(errors.length > 0 ? errors : errData?.data?.message || "Failed to create contractor")
        return
      }

      const newId = res.data?.data?.id
      if (newId == null) {
        toast.error("Contractor was created but no id was returned.")
        return
      }

      if (partnersFormRef.current) {
        setIsSavingPartners(true)
        try {
          const pr = await partnersFormRef.current.savePartners(newId)
          if (!pr.ok) return
        } finally {
          setIsSavingPartners(false)
        }
      }

      toast.success("Contractor has been successfully added!")
      onSuccess()
    } catch (error) {
      console.error(error)
    }
  }

  const onCancelClick = () => {
    dispatch(setCurrentZip(undefined))
    onCancel()
  }

  const isDrawerBusy = isFetching || isSavingPartners

  return (
    <>
      {createPortal(
        <ModalLayout
          onClose={onCancelClick}
          closeIconClassName={styles.closeIconClassName}
          isDrawer
          isWithoutFooter
          isFetching={isDrawerBusy}
        >
          <ModalHeader title="Add Contractor" borderless withShadow>
            <IconContractor />
          </ModalHeader>
          <ContractorModalContent
            contractor={inputValuesInitialState}
            partnersFormRef={partnersFormRef}
            onSubmit={onSubmit}
            isCreate
          />
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
